import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getCurrencySymbol } from "../../../../constants/currency";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  MenuItem,
  Autocomplete,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Fab,
} from "@mui/material";
import {
  PlusIcon,
  XMarkIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { Edit, Save } from "@mui/icons-material";
import { OrderData, OrderPayload, ProductConfiguration } from "../types";
import { Order, Product } from "../../order";
import { PRIORITY_OPTIONS } from "../constants";
import { orderService } from "../../orderService";
import temporaryNumberingService from "../../../../services/temporaryNumberingService";
import ProductTable from "./ProductTable";
import SitesFormModal from "../../../site/SitesFormModal";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { usePermission } from "../../../../components/common/PermissionGuard";

const PAYMENT_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "partial", label: "Partial Paid" },
  { value: "paid", label: "Fully Paid" },
  // { value: 'overdue', label: 'Overdue' }
];

interface IncrementalOrderFormProps {
  selectedCustomer: any;
  selectedSite: any;
  customers: any[];
  sites: any[];
  onCustomerChange: (customer: any) => void;
  onSiteChange: (site: any) => void;
  onProductAdded: (product: Product) => void;
  onProductEdit?: (product: Product) => void;
  onOrderCreated: (order: OrderPayload) => void;
  onOrderUpdated?: (order: any) => void;
  onIncrementalProductComplete?: (product: Product) => void;
  configuredProducts?: Product[];
  existingOrder?: any;
  isEditMode?: boolean;
}

const IncrementalOrderForm: React.FC<IncrementalOrderFormProps> = ({
  selectedCustomer,
  selectedSite,
  customers,
  sites,
  onCustomerChange,
  onSiteChange,
  onProductAdded,
  onProductEdit,
  onOrderCreated,
  onOrderUpdated,
  onIncrementalProductComplete,
  configuredProducts = [],
  existingOrder,
  isEditMode = false,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // Permission check for order edit
  const canEditOrder = usePermission('orders', 'update');
  
  // Permission check for site creation
  const canCreateSite = usePermission('sites', 'create');
  
  const [shouldUpdateMetaData, setShouldUpdateMetaData] = useState(false);
  // const [editMode, setEditMode] = useState(true);
  // const [enableData, setEnableData] = useState(true)
  const [orderData, setOrderData] = useState<OrderData>({
    amount: "",
    paymentStatus: "pending",
    priority: "medium",
    dueDate: "",
    notes: "",
  });

  // useEffect(() => {
  //   setEditMode(isEditMode);
  // }, [isEditMode]);

  useEffect(() => {
    console.log("selected customer========:", selectedCustomer);
    console.log("selected selectedSite========:", selectedSite);
    console.log("isEditMode========:", isEditMode);
  }, [selectedCustomer,isEditMode,selectedSite]);

  // Initialize form data from existing order in edit mode
  useEffect(() => {
    console.log("🔍 IncrementalOrderForm useEffect debug:", {
      isEditMode: isEditMode,
      existingOrder: existingOrder,
      existingOrderProducts: existingOrder?.products?.length || 0,
      willSetIsOrderCreated: isEditMode && existingOrder,
    });

    if (isEditMode && existingOrder) {
      console.log(
        "🔄 Initializing IncrementalOrderForm with existing order:",
        existingOrder
      );
      console.log("🔄 Order products:", existingOrder.products);

      // Initialize form data
      let formattedDueDate = "";
      if (existingOrder.dueDate) {
        const date = new Date(existingOrder.dueDate);
        // Check if date is valid before calling toISOString()
        if (date instanceof Date && !isNaN(date.getTime())) {
          formattedDueDate = date.toISOString().split("T")[0];
        }
      }

      setOrderData({
        amount: existingOrder.totalAmount?.toString() || "",
        paymentStatus: existingOrder.paymentStatus || "pending",
        priority: existingOrder.priority || "medium",
        dueDate: formattedDueDate,
        notes: existingOrder.notes || "",
      });

      // Initialize current order state
      setCurrentOrder(existingOrder);
      setIsOrderCreated(true);

      // Clear local products since they're now in the order
      setProducts([]);

      console.log("🔄 Set currentOrder:", existingOrder);
      console.log("🔄 Set isOrderCreated to true");
    }
  }, [isEditMode, existingOrder]);

  const [products, setProducts] = useState<Product[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentOrder, setCurrentOrder] = useState<any>(null);
  const [isOrderCreated, setIsOrderCreated] = useState(false);
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [openCreateSiteModal, setOpenCreateSiteModal] = useState(false);
  const createSiteOption = { id: "__create_new__", name: "Create New Site" };
  // Only include "Create New Site" option if user has permission
  const siteOptions = canCreateSite ? [...sites, createSiteOption] : sites;
  const [selectCustomerId, setSelectCustomerId] = useState<string | null>(null);

  useEffect(() => {
    console.log("🔍selected customer id========:", selectCustomerId);
    console.log("=================orderData",orderData)
    console.log("==================products",products)
  }, [selectCustomerId,products,orderData]);

  // Combine local products with configured products and order products (memoized to prevent unnecessary re-renders)
  const allProducts = useMemo(() => {
    const orderProducts = currentOrder?.products || [];

    // Debug logging
    console.log("🔄 IncrementalOrderForm allProducts calculation:", {
      hasCurrentOrder: !!currentOrder,
      orderProductsCount: orderProducts.length,
      localProductsCount: products.length,
      configuredProductsCount: configuredProducts.length,
      isOrderCreated,
      refreshTrigger,
    });

    // If we have an existing order, use its products; otherwise use local + configured products
    if (currentOrder && orderProducts.length > 0) {
      console.log("🔄 Using order products:", orderProducts.length);
      return orderProducts;
    } else {
      console.log(
        "🔄 Using local + configured products:",
        products.length + configuredProducts.length
      );
      return [...products, ...configuredProducts];
    }
  }, [
    products,
    configuredProducts,
    currentOrder?.products,
    isOrderCreated,
    refreshTrigger,
  ]);

  // Check if we can create order (has customer, site, and at least one product)
  const canCreateOrder =
    selectedCustomer && selectedSite && allProducts.length > 0;

  // Check if we can add more products (order exists and is not completed)
  const canAddProducts =
    currentOrder &&
    currentOrder.status !== "completed" &&
    currentOrder.status !== "dispatched";

  const handleAddProduct = () => {
    console.log("🔄 handleAddProduct called - currentOrder:", currentOrder);
    console.log("🔄 Order ID:", currentOrder?.id);
    console.log("🔄 isOrderCreated:", isOrderCreated);

    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substr(2, 9);
    const newProduct: Product = {
      id: `${timestamp}-${randomSuffix}`,
      product_tracking_code: `PRD-${timestamp}-${randomSuffix}`,
      order_id: currentOrder?.id || "",
      order_tracking_code: currentOrder?.orderNumberNew || "",
      customer_tracking_code: selectedCustomer?.customerId || "",
      type: "door",
      name: "", // Will be set by user in the configuration panel
      total_items: 0,
      completed_items: 0,
      quantity: 1,
      items: [],
      frame_count: 0,
      panel_count: 0,
      panel_window_count: 0,
      net_count: 0,
    };

    // DON'T add to local products state immediately - wait until configuration is complete
    // setProducts(prev => [...prev, newProduct]); // ← Removed for Option 1

    // DON'T trigger refresh for empty product - wait until configuration is complete
    // setRefreshTrigger(prev => prev + 1); // ← Removed for Option 1

    // Just call parent callback to open configuration panel
    onProductAdded(newProduct);
  };

  const handleSiteChange = (event, newValue) => {
    if (newValue && newValue.id === "__create_new__") {
      setOpenCreateSiteModal(true);
    } else {
      onSiteChange(newValue);
    }
  };

  const handleSiteCreated = (site: any) => {
    console.log("Site created successfully from Customers component:", site);
    // Close the create modal
    setOpenCreateSiteModal(false);
    setSelectCustomerId(null);
    // Refresh the customers list to show updated data
  };

  const handleProductUpdate = (updatedProduct: Product) => {
    const updatedProducts = products.map((p) =>
      p.id === updatedProduct.id ? updatedProduct : p
    );
    setProducts(updatedProducts);
  };

  // Create order with first product (incremental approach)
  const handleCreateOrderWithFirstProduct = async (
    productToCreate?: Product
  ) => {
    console.trace(" 🚨 handleSubmit stack");

    console.log("🔄 handleCreateOrderWithFirstProduct called");
    console.log("🔄 productToCreate:", productToCreate);
    console.log("🔄 canCreateOrder:", canCreateOrder);
    console.log("🔄 selectedCustomer:", selectedCustomer);
    console.log("🔄 selectedSite:", selectedSite);
    console.log("🔄 allProducts.length:", allProducts.length);
    console.log("🔄 allProducts:", allProducts);

    // Use the passed product or fall back to allProducts[0]
    const firstProduct = productToCreate || allProducts[0];

    if (!selectedCustomer || !selectedSite || !firstProduct) {
      console.log("❌ Cannot create order - missing requirements");
      setError("Please select a customer, site, and add at least one product");
      return;
    }

    if (!orderData.dueDate) {
      console.log("❌ Cannot create order - missing due date");
      setError("Please select a due date");
      return;
    }

    console.log("✅ Creating order with first product...");
    setIsProcessing(true);
    setError(null);

    try {
      // Transform product to the format expected by the API
      const productPayload = {
        name: firstProduct.name,
        type: firstProduct.type,
        evaOrder: firstProduct.evaOrder,
        label: firstProduct.label,
        location: firstProduct.location,
        notes: firstProduct.notes,
        // Remove description as it's not part of the Product interface
        specifications: firstProduct.specifications,
        itemTypes: transformProductToItemTypes(firstProduct),
      };

      // Create order with first product using incremental approach
      const orderPayload = {
        customerId: selectedCustomer.id,
        siteId: selectedSite.id,
        totalAmount: parseFloat(orderData.amount) || 0,
        priority: orderData.priority,
        dueDate: orderData.dueDate,
        notes: orderData.notes,
        orderLabel: `Order-${Date.now()}`,
        orderNotes: orderData.notes,
        paymentStatus: orderData.paymentStatus,
        firstProduct: productPayload,
      };

      console.log(
        "🔄 Calling createOrderWithFirstProduct API with payload:",
        orderPayload
      );
      const response = await orderService.createOrderWithFirstProduct(
        orderPayload
      );
      console.log("🔄 API response:", response);

      if (response.success) {
        console.log("✅ Order created successfully:", response.data.order);
        setCurrentOrder(response.data.order);
        setIsOrderCreated(true);
        setProducts([]); // Clear local products since they're now in the order

        // Notify parent component about the created order
        if (onOrderCreated) {
          onOrderCreated(response.data.order);
        }

        // Trigger refresh to ensure UI updates
        setRefreshTrigger((prev) => prev + 1);

        // Show success message
        setError(null);
        navigate(`/orders/edit/${response.data.order.id}`);

        // Redirect to edit order screen after a short delay
        // setTimeout(() => {
        //   console.log(
        //     "🔄 Redirecting to edit order screen:",
        //     `/orders/edit/${response.data.order.id}`
        //   );
        //   navigate(`/orders/edit/${response.data.order.id}`);
        // }, 2000); // 2 second delay to show success message
      } else {
        console.error("❌ Order creation failed:", response.error);
        throw new Error(response.error?.message || "Failed to create order");
      }
    } catch (error) {
      console.error("❌ Error creating order:", error);
      setError(
        `Failed to create order: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  //update order

  //edit order details
  const handleOrderEdit = async (id: string, orderData: any) => {
    console.log("🔄 Editing order:", id, orderData);

    const orderPayload = {
      customerId: selectedCustomer?.id,
      siteId: selectedSite?.id,
      totalAmount: parseFloat(orderData.amount) || 0,
      priority: orderData.priority,
      dueDate: orderData.dueDate,
      notes: orderData.notes,
      orderLabel: `Order-${Date.now()}`,
      orderNotes: orderData.notes,
      paymentStatus: orderData.paymentStatus,
    };
    console.log(
      "🔄 Calling createOrderWithFirstProduct API with payload:",
      orderPayload
    );
    const response = await orderService.updateOrder(id, orderPayload);
    console.log("✅ Order updated successfully:", response);
    return response;
  }

  //create order only with details
  const createOrderOnly = async () => {
    if (!orderData.dueDate) {
      setError("Please select a due date");
      return;
    }

    const orderPayload = {
      customerId: selectedCustomer?.id,
      siteId: selectedSite?.id,
      totalAmount: parseFloat(orderData.amount) || 0,
      priority: orderData.priority,
      dueDate: orderData.dueDate,
      notes: orderData.notes,
      orderLabel: `Order-${Date.now()}`,
      orderNotes: orderData.notes,
      paymentStatus: orderData.paymentStatus,
    };
    console.log("🔄 Creating order with payload:", orderPayload);  
    const response = await orderService.createOrder(orderPayload);
    console.log("✅ Order created successfully:", response);

      if (response.success) {
        console.log("✅ Order created successfully:", response.data.order);
        setCurrentOrder(response.data.order);
        setIsOrderCreated(true);
        setProducts([]); // Clear local products since they're now in the order

        // Notify parent component about the created order
        if (onOrderCreated) {
          onOrderCreated(response.data.order);
        }

        // Trigger refresh to ensure UI updates
        setRefreshTrigger((prev) => prev + 1);

        // Show success message
        setError(null);
        navigate(`/orders/edit/${response.data.order.id}`);
        // Redirect to edit order screen after a short delay
        // setTimeout(() => {
        //   console.log(
        //     "🔄 Redirecting to edit order screen:",
        //     `/orders/edit/${response.data.order.id}`
        //   );
        //   navigate(`/orders/edit/${response.data.order.id}`);
        // }, 2000); // 2 second delay to show success message
      } else {
        console.error("❌ Order creation failed:", response.error);
        throw new Error(response.error?.message || "Failed to create order");
      }

    return response;
  }


  // Add additional product to existing order
  const handleAddProductToOrder = async (product: Product) => {
    console.log("🔄 handleAddProductToOrder called with product:", product);
    console.log("🔄 currentOrder:", currentOrder);
    console.log("🔄 currentOrder.id:", currentOrder?.id);

    if (!currentOrder) {
      console.error("❌ No active order found");
      setError("No active order found");
      return;
    }

    if (!currentOrder.id) {
      console.error("❌ No order ID found in currentOrder");
      setError("No order ID found");
      return;
    }

    // Allow duplicate products - removed duplicate checking

    setIsAddingProduct(true);
    setError(null);

    try {
      // Transform product to the format expected by the API
      const productPayload = {
        name: product.name,
        type: product.type,
        evaOrder: product.evaOrder,
        label: product.label,
        location: product.location,
        notes: product.notes,
        color: product.color,
        specifications: product.specifications,
        itemTypes: transformProductToItemTypes(product),
        // Include configuration data with count information
        configuration: (product.metadata as any)?.configuration || {
          label: product.label,
          location: product.location,
          productType: product.type,
          color: product.color,
          frameChecked: true,
          frameCount: 0,
          panelCount: 0,
          panelWindowCount: 0,
          netCount: 0,
          frameEntries: [],
          panelEntries: [],
          panelWindowEntries: [],
          netEntries: [],
        },
      };

      // Add product to existing order
      console.log(
        "🔄 Calling addProductToOrder with order ID:",
        currentOrder.id
      );
      console.log("🔄 Product payload:", productPayload);
      const response = await orderService.addProductToOrder(
        currentOrder.id,
        productPayload
      );

      if (response.success) {
        // Refresh order data to get updated totals
        const orderResponse = await orderService.getOrder(currentOrder.id);
        if (orderResponse.success) {
          setCurrentOrder(orderResponse.data);

          // Notify parent component about the updated order
          if (onOrderUpdated) {
            onOrderUpdated(orderResponse.data);
          }
        }

        // Remove the product from local state since it's now in the order
        setProducts((prev) => prev.filter((p) => p.id !== product.id));

        // Trigger a refresh of the product list
        setRefreshTrigger((prev) => prev + 1);

        setError(null);
        console.log("Product added successfully:", response.data.product);
      } else {
        throw new Error(
          response.error?.message || "Failed to add product to order"
        );
      }
    } catch (error: any) {
      console.error("Error adding product to order:", error);
      setError("Failed to add product to order. Please try again.");
    } finally {
      setIsAddingProduct(false);
    }
  };



  const handleSave = async () => {
    console.error("order id for update",id);
    try {
      await handleOrderEdit(id, orderData);
      // alert("Data updated successfully!");
      setShouldUpdateMetaData(false);
    } catch (error) {
      console.error("❌ Failed to update order:", error);
      // alert("Failed to update order. Please try again.");
    }
  };

  const handleOrderCreate = async () => {
    try {
      await createOrderOnly();
      setShouldUpdateMetaData(false);
    } catch (error) {
      console.error("❌ Failed to update order:", error);

    }
  }

  const handleEdit = () => {
    setShouldUpdateMetaData(true); 
  };

  const handleCreate = () => {
    setShouldUpdateMetaData(true);
  }


  // Transform product to itemTypes format for API
  const transformProductToItemTypes = (product: Product) => {
    const itemTypes: Record<string, any[]> = {
      frame: [],
      panel: [],
      panelWindow: [],
      net: [],
      openableType: [],
      door: [],
    };

    // Helper function to convert various boolean representations to proper boolean
    const toBoolean = (value: any, defaultValue: boolean): boolean => {
      if (value === null || value === undefined) return defaultValue;
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value !== 0;
      if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
      return defaultValue;
    };

    // Transform items based on their type - use the updated items from the product
    product.items?.forEach((item) => {
      // Ensure width and height are integers, not decimals
      const rawWidth =
        (item as any)?.width || (item.specifications as any)?.width || 0;
      const rawHeight =
        (item as any)?.height || (item.specifications as any)?.height || 0;

      // Convert to integer, handling both string and number inputs
      const width = Math.round(parseFloat(String(rawWidth))) || 0;
      const height = Math.round(parseFloat(String(rawHeight))) || 0;

      // Extract clean item number (e.g., "F218" from "Door-147-218")
      const extractCleanNumber = (item: any) => {
        const number = (item as any)?.number || item.itemNumber;
        if (number) return number;

        // If using item.id, try to extract the clean number format
        const id = item.id;
        if (id && typeof id === "string") {
          // Look for patterns like "F218", "P123", "PW456", "N789"
          const match = id.match(/([FPWNPW]+\d+)/);
          if (match) return match[1];

          // If it's in format "Door-147-218", extract the last part and add F prefix
          const parts = id.split("-");
          if (parts.length >= 3) {
            const lastPart = parts[parts.length - 1];
            if (/^\d+$/.test(lastPart)) {
              return `F${lastPart}`;
            }
          }
        }

        return null;
      };

      const itemData: any = {
        width: width,
        height: height,
        quantity: item.quantity || 1,
        number: extractCleanNumber(item),
        specifications: {
          ...item.specifications,
          material: item.specifications?.material || "Aluminum",
          color: item.specifications?.color || "Silver",
          finish: (item.specifications as any)?.finish || "Standard",
        },
        assigned_to: item.assignedTo,
      };
      
      // Include item ID if available, or set alreadyExist flag for existing items
      if (item.id) {
        itemData.id = item.id;
      } else if (extractCleanNumber(item)) {
        // If item has a number but no ID, it's likely an existing item
        itemData.alreadyExist = true;
      }

      // Map item types to the correct category
      switch (item.type as any) {
        case "Frame":
          itemTypes.frame.push(itemData);
          break;
        case "Panel":
          itemTypes.panel.push({
            ...itemData,
            // Read actual values from item, with proper defaults
            bar_required: toBoolean((item as any)?.bar_required ?? (item.specifications as any)?.bar_required, false),
            member_required: toBoolean((item as any)?.member_required ?? (item.specifications as any)?.member_required, true),
            glass_required: toBoolean((item as any)?.glass_required ?? (item.specifications as any)?.glass_required, true),
            is_laser_cutting: toBoolean((item as any)?.is_laser_cutting, false),
          });
          break;
        case "Panel Window":
          itemTypes.panelWindow.push(itemData);
          break;
        case "Net Fixing":
          itemTypes.net.push(itemData);
          break;
        case "Openable Type":
          itemTypes.openableType.push(itemData);
          break;
        case "Door":
          itemTypes.door.push(itemData);
          break;
        default:
          itemTypes.frame.push(itemData);
      }
    });

    return itemTypes;
  };


  const handleProductConfigurationComplete = async (
    updatedProduct: Product
  ) => {
    // Call the parent callback first if provided
    if (onIncrementalProductComplete) {
      onIncrementalProductComplete(updatedProduct);
    }

    if (isOrderCreated || currentOrder) {
      // Order exists - add product to backend
      console.log(
        "🔄 Order exists - adding product to backend:",
        updatedProduct.name
      );
      await handleAddProductToOrder(updatedProduct);
    } else {
      // No order exists yet - create order with first product
      console.log(
        "🔄 No order exists - creating order with first product:",
        updatedProduct.name
      );
      await handleCreateOrderWithFirstProduct(updatedProduct);
    }
  };

  // Override the onProductAdded to handle incremental mode
  const handleProductAddedIncremental = (product: Product) => {
    onProductAdded(product);
  };

  const siteInputRef = useRef<HTMLInputElement>(null);
  const handleCustomerChange = (event: any, newValue: any) => {
    onCustomerChange(newValue);
    // Focus the site input after customer is selected
    setTimeout(() => {
      siteInputRef.current?.focus();
    }, 100); // slight delay to ensure UI updates
  };

  const handleCancelOrder = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      // Clear all state
      setProducts([]);
      setCurrentOrder(null);
      setIsOrderCreated(false);
      setOrderData({
        amount: "",
        paymentStatus: "pending",
        priority: "medium",
        dueDate: "",
        notes: "",
      });
    } catch (error) {
      console.error("Error cancelling order:", error);
      setError("Failed to cancel order. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateSite = (customerId: string) => {
    setSelectCustomerId(customerId);
    console.log(
      "Creating site for customer ID from handleCreateSite function:",
      selectCustomerId
    );
    setOpenCreateSiteModal(true);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Order Details Section */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{display:"flex", justifyContent:'space-between'}}>
          { isEditMode ? (
            <Box sx={{display:'flex'}}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", mb: 3, mr:3 }}
              >
                {selectedCustomer?.name}
              </Typography>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
              >
                {selectedSite?.name}
              </Typography>
            </Box>
          ):(
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
            >
              📋 Order Information
              {isOrderCreated && (
                <Chip
                  icon={<CheckCircleIcon className="h-4 w-4" />}
                  label="Order Created"
                  color="success"
                  size="small"
                />
              )}
            </Typography>
          )            
          }
          
          <Box sx={{mb: 1}}>
            { isEditMode ? (
              <>
              {shouldUpdateMetaData ? (
                 <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSave}
                  sx={{ mt: 2 }}
                >
                  Save
                </Button> 
              ) : (
                  canEditOrder && (
                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={handleEdit}
                      sx={{ mt: 2 }}
                    >
                      Edit
                    </Button>
                  )
              )}
              </>
              ):(
                <>                  
                  {/* Ensure fields are enabled immediately */}
                  {!shouldUpdateMetaData && setShouldUpdateMetaData(true)}
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleOrderCreate}
                    sx={{ mt: 2 }}
                  >
                    Save
                  </Button>                
                </>
              )
            }            
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 2,
            mb: 3,
          }}
        >
          {
            isEditMode ? (
              shouldUpdateMetaData && (
                <>
                  <Autocomplete
                    options={customers}
                    getOptionLabel={(option) => option.name || ""}
                    value={selectedCustomer}
                    // onChange={(event, newValue) => onCustomerChange(newValue)}
                    onChange={handleCustomerChange}
                    // disabled={isOrderCreated}
                    disabled = {shouldUpdateMetaData}
                    renderInput={(params) => {
                      const isFocused =
                        document.activeElement &&
                        document.activeElement.id === params.inputProps.id;
                      return (
                        <TextField
                          {...params}
                          label="Select Customer"
                          required
                          size="small"
                          // error={!selectedCustomer}
                          // helperText={!selectedCustomer ? "Please select a customer" : ""}
                          error={isFocused && !selectedSite}
                          helperText={!selectedCustomer && "Select a customer first"}
                        />
                      );
                    }}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <Box component="li" key={key} {...otherProps}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {option.name}
                            </Typography>
                            {option.email && (
                              <Typography variant="caption" color="text.secondary">
                                {option.email}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    }}
                  />

                  <Autocomplete
                    options={siteOptions}
                    getOptionLabel={(option) => option.name || ""}
                    value={selectedSite}
                    // onChange={(event, newValue) => onSiteChange(newValue)}
                    onClick={(e) => {
                      e.stopPropagation();
                      // handleEditCustomer(customer);
                      handleCreateSite(selectedCustomer.id);
                    }}
                    onChange={handleSiteChange}
                    // disabled={!selectedCustomer || isOrderCreated}
                    disabled = {!shouldUpdateMetaData}
                    renderInput={(params) => {
                      const isFocused =
                        document.activeElement &&
                        document.activeElement.id === params.inputProps.id;
                      return (
                        <TextField
                          {...params}
                          label="Select Site"
                          required
                          size="small"
                          inputRef={siteInputRef}
                          error={isFocused && !selectedSite}
                          helperText={
                            isFocused && !selectedSite ? "Please select a site" : ""
                          }
                          // error={!selectedSite}
                          // helperText={!selectedCustomer ? "Select a customer first" : !selectedSite ? "Please select a site" : ""}
                        />
                      );
                    }}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      if (option.id === "__create_new__") {
                        return (
                          <Box
                            component="li"
                            key={key}
                            {...otherProps}
                            sx={{ color: "primary.main", fontWeight: 600 }}
                          >
                            {option.name}
                          </Box>
                          // <Box component="li" {...props}>
                          //   <Typography
                          //     variant="body2"
                          //     sx={{
                          //       fontWeight: 600 ,

                          //     }}
                          //   >{option.name}</Typography>
                          // </Box>
                        );
                      }
                      return (
                        <Box component="li" key={key} {...otherProps}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {option.name}
                            </Typography>
                            {option.address && (
                              <Typography variant="caption" color="text.secondary">
                                {option.address}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    }}
                  />
                </>
                    )                
                  ):(
                    <>
                      <Autocomplete
                        options={customers}
                        getOptionLabel={(option) => option.name || ""}
                        value={selectedCustomer}
                        // onChange={(event, newValue) => onCustomerChange(newValue)}
                        onChange={handleCustomerChange}
                        // disabled={isOrderCreated}
                        disabled = {!shouldUpdateMetaData}
                        renderInput={(params) => {
                          const isFocused =
                            document.activeElement &&
                            document.activeElement.id === params.inputProps.id;
                          return (
                            <TextField
                              {...params}
                              label="Select Customer"
                              required
                              size="small"
                              // error={!selectedCustomer}
                              // helperText={!selectedCustomer ? "Please select a customer" : ""}
                              error={isFocused && !selectedSite}
                              helperText={!selectedCustomer && "Select a customer first"}
                            />
                          );
                        }}
                        renderOption={(props, option) => {
                          const { key, ...otherProps } = props;
                          return (
                            <Box component="li" key={key} {...otherProps}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {option.name}
                                </Typography>
                                {option.email && (
                                  <Typography variant="caption" color="text.secondary">
                                    {option.email}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          );
                        }}
                      />

                      <Autocomplete
                        options={siteOptions}
                        getOptionLabel={(option) => option.name || ""}
                        value={selectedSite}
                        // onChange={(event, newValue) => onSiteChange(newValue)}
                        onClick={(e) => {
                          e.stopPropagation();
                          // handleEditCustomer(customer);
                          handleCreateSite(selectedCustomer.id);
                        }}
                        onChange={handleSiteChange}
                        // disabled={!selectedCustomer || isOrderCreated}
                        disabled = {!shouldUpdateMetaData}
                        renderInput={(params) => {
                          const isFocused =
                            document.activeElement &&
                            document.activeElement.id === params.inputProps.id;
                          return (
                            <TextField
                              {...params}
                              label="Select Site"
                              required
                              size="small"
                              inputRef={siteInputRef}
                              error={isFocused && !selectedSite}
                              helperText={
                                isFocused && !selectedSite ? "Please select a site" : ""
                              }
                              // error={!selectedSite}
                              // helperText={!selectedCustomer ? "Select a customer first" : !selectedSite ? "Please select a site" : ""}
                            />
                          );
                        }}
                        renderOption={(props, option) => {
                          const { key, ...otherProps } = props;
                          if (option.id === "__create_new__") {
                            return (
                              <Box
                                component="li"
                                key={key}
                                {...otherProps}
                                sx={{ color: "primary.main", fontWeight: 600 }}
                              >
                                {option.name}
                              </Box>
                              // <Box component="li" {...props}>
                              //   <Typography
                              //     variant="body2"
                              //     sx={{
                              //       fontWeight: 600 ,

                              //     }}
                              //   >{option.name}</Typography>
                              // </Box>
                            );
                          }
                          return (
                            <Box component="li" key={key} {...otherProps}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {option.name}
                                </Typography>
                                {option.address && (
                                  <Typography variant="caption" color="text.secondary">
                                    {option.address}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          );
                        }}
                      />
                    </>
                  )
                }
          
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 2,
          }}
        >
          <TextField
            label="Amount (Rs)"
            value={orderData.amount}
            // onChange={(e) =>
            //   setOrderData({ ...orderData, amount: e.target.value })
            // }
            onChange={(e) => {
              const value = e.target.value;
              // Allow only digits, optional single decimal point
              if (/^\d*\.?\d*$/.test(value)) {
                setOrderData({ ...orderData, amount: value });
              }
            }}
            disabled = {!shouldUpdateMetaData}
            fullWidth
            required
            size="small"
            // type="number"
            type="text"
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{
              startAdornment: (
                <Typography sx={{ mr: 1, color: "text.secondary" }}>
                  {getCurrencySymbol()}
                </Typography>
              ),
            }}
          />

          <TextField
            select
            label="Payment Status"
            value={orderData.paymentStatus}
            onChange={(e) =>
              setOrderData({
                ...orderData,
                paymentStatus: e.target.value as any,
              })
            }
            disabled = {!shouldUpdateMetaData}
            fullWidth
            required
            size="small"
          >
            {PAYMENT_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Priority"
            value={orderData.priority}
            onChange={(e) =>
              setOrderData({ ...orderData, priority: e.target.value as any })
            }
            disabled = {!shouldUpdateMetaData}
            fullWidth
            size="small"
          >
            {PRIORITY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          {/* <TextField
            label="Due Date"
            type="date"
            value={orderData.dueDate}
            onChange={(e) =>
              setOrderData({ ...orderData, dueDate: e.target.value })
            }
            disabled = {!shouldUpdateMetaData}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: new Date().toISOString().split("T")[0], // disables all past dates
            }}
          /> */}

          <DatePicker
            label="Due Date"
            value={orderData.dueDate ? dayjs(orderData.dueDate, "YYYY-MM-DD") : null}
            onChange={(newValue) => {
              setOrderData({
                ...orderData,
                // Store in backend-friendly format (YYYY-MM-DD)
                dueDate: newValue ? dayjs(newValue).format("YYYY-MM-DD") : "",
              });
            }}
            disabled={!shouldUpdateMetaData}
            minDate={dayjs()} // disable past dates
            required
            slotProps={{
              textField: {
                fullWidth: true,
                size: "small",
                required: true,
                // Display format in input
                inputProps: { placeholder: "DD/MM/YYYY" },
              },
            }}
          />
          
        </Box>

        {/* <TextField
          label="Notes"
          value={orderData.notes}
          onChange={(e) => setOrderData({ ...orderData, notes: e.target.value })}
          fullWidth
          multiline
          rows={2}
          size="small"
          sx={{ mt: 2 }}
        /> */}
      </Paper>

      {/* Products Section */}
      {
        isEditMode && (
          <Paper sx={{ p: 3, display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="h6">
                🏗️ Set(s){" "}
                {/* {isOrderCreated
                  ? "(Add More Products)"
                  : "(Configure First Product)"} */}
              </Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                {allProducts.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {allProducts.length} product
                    {allProducts.length !== 1 ? "s" : ""} configured
                  </Typography>
                )}
                
                  <Button
                    variant="contained"
                    startIcon={<PlusIcon className="h-4 w-4" />}
                    onClick={handleAddProduct}
                    disabled={isAddingProduct}
                    sx={{
                      minWidth: 140,
                      fontWeight: 600,
                    }}
                  >
                    {isOrderCreated ? "Add More Sets" : "Add Set"}
                    {/* Add More Products */}
                  </Button>
                
              </Box>
            </Box>

            {allProducts.length === 0 ? (
              // <Box
              //   sx={{
              //     display: "flex",
              //     flexDirection: "column",
              //     alignItems: "center",
              //     justifyContent: "center",
              //     flex: 1,
              //     textAlign: "center",
              //     py: 4,
              //   }}
              // >
              //   <Typography variant="h6" color="text.secondary" gutterBottom>
              //     {isOrderCreated
              //       ? "Add more products to your order"
              //       : "Add your first product to create the order"}
              //   </Typography>
              //   <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              //     {isOrderCreated
              //       ? 'Click "Add Product" to add more products to your existing order'
              //       : "Configure your first product to get started with order creation"}
              //   </Typography>
              //   {!isOrderCreated && (
              //     <Button
              //       variant="contained"
              //       startIcon={<PlusIcon className="h-4 w-4" />}
              //       onClick={handleAddProduct}
              //       size="large"
              //     >
              //       Add First Product
              //     </Button>
              //   )}
              // </Box>
              <></>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  overflow: "visible",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <ProductTable
                  key={`product-table-${refreshTrigger}`}
                  products={allProducts}
                  onEditProduct={onProductEdit || onProductAdded}
                  onRemoveProduct={(productId) =>
                    setProducts((prev) => prev.filter((p) => p.id !== productId))
                  }
                  onAddProduct={handleAddProduct}
                  canEdit={true}
                  canRemove={() => !isOrderCreated}
                />
              </Box>
            )}
          </Paper>
        )
      }
      

      {/* Action Buttons */}
      {/* <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleCancelOrder}
            disabled={isProcessing}
          >
            {isEditMode ? 'Cancel' : (isOrderCreated ? 'Reset Order' : 'Cancel')}
          </Button>
          
          {isEditMode ? (
            <Button
              variant="contained"
              onClick={() => onOrderUpdated && onOrderUpdated(existingOrder)}
              startIcon={<CheckCircleIcon className="h-4 w-4" />}
            >
              Save Changes
            </Button>
          ) : !isOrderCreated ? (
            <Button
              variant="contained"
              onClick={() => handleCreateOrderWithFirstProduct()}
              disabled={!canCreateOrder || isProcessing}
              startIcon={isProcessing ? <CircularProgress size={16} /> : <ArrowRightIcon className="h-4 w-4" />}
            >
              {isProcessing ? 'Creating Order...' : 'Create Order'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => onOrderCreated(currentOrder)}
              startIcon={<CheckCircleIcon className="h-4 w-4" />}
            >
              View Order Details
            </Button>
          )}
        </Box>
      </Paper> */}

      {openCreateSiteModal && (
        <SitesFormModal
          isOpen={openCreateSiteModal}
          onClose={() => setOpenCreateSiteModal(false)}
          sites={null}
          customerId={selectedCustomer?.id || ""}
          onSuccess={handleSiteCreated}
        />
      )}
    </Box>
  );
};

export default IncrementalOrderForm;
